import React, {
  createContext,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
} from "react";
import * as apiInit from "../utils/ApiInit.ts";
import Icon from "../components/widgets/Icon";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import ErrorUsuAdNoBd from "../components/static-pages/errorUsuAdNoBd.tsx";

interface InitDataContextInterface {
  initData: any;
  setInitData: Dispatch<SetStateAction<any>>;
  menus: any;
  setMenus: Dispatch<SetStateAction<any>>;
  isTutorialActive: boolean;
  setIsTutorialActive: Dispatch<SetStateAction<any>>;
  parametros: any;
  noUserInBd: boolean;
}

const defaultState: InitDataContextInterface = {
  initData: {},
  setInitData: () => {},
  menus: [],
  setMenus: () => [],
  isTutorialActive: false,
  setIsTutorialActive: () => {},
  parametros: [],
  noUserInBd: false,
};

const InitDataContext = createContext<InitDataContextInterface>(defaultState);

type InitDataProviderProps = {
  children: React.ReactNode;
};

const InitDataProvider = ({ children }: InitDataProviderProps) => {
  const [menus, setMenus] = useState<any[]>([]);
  const [initData, setInitData] = useState({});
  const [parametros, setParametros] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isTutorialActive, setIsTutorialActive] = useState(true);
  const [noUserInBd, setNoUserInBd] = useState(false);

  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts, inProgress } = useMsal();

  const appIdBackend = import.meta.env.VITE_AD_BACKEND_ID;
  const ENVIRONMENT = import.meta.env.VITE_MODE;

  const protectedApiRequest = {
    scopes: [`api://${appIdBackend}/acceso.completo`],
  };

  // Sync Tutorial state once on mount
  useEffect(() => {
    const isTutorialActiveVar = localStorage.getItem("isTutorialActive");
    if (isTutorialActiveVar === "false") {
      setIsTutorialActive(false);
    } else {
      setIsTutorialActive(true);
    }
  }, []);

  // Main Auth & Initialization Logic
  useEffect(() => {
    // 1. Si MSAL está procesando algo (login, redirect, tokens), esperamos.
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    // 2. Si MSAL terminó pero no estamos autenticados, cancelamos carga.
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    // 3. Si dice estar autenticado pero 'accounts' aún está vacío,
    // esperamos al siguiente render para evitar romper el flujo.
    if (accounts.length === 0) {
      return;
    }

    let isMounted = true;

    const initializeData = async () => {
      try {
        // Usamos la cuenta activa oficial de MSAL en lugar de sessionStorage
        const currentAccount = instance.getActiveAccount() || accounts[0];

        // Intentar obtener el email directamente del token de MSAL
        let userEmail = currentAccount?.username || "";

        // Si por alguna razón necesitas el fallback de tu sessionStorage anterior:
        if (!userEmail) {
          const userAdData = sessionStorage.getItem("user-details");
          if (userAdData) {
            try {
              userEmail = JSON.parse(userAdData).email;
            } catch (e) {
              console.error("Error parsing user-details", e);
            }
          }
        }

        if (ENVIRONMENT === "production") {
          if (!userEmail) {
            throw new Error(
              "No se pudo determinar el email del usuario logueado.",
            );
          }

          // Adquirir token de forma segura usando la cuenta provista por MSAL
          const accessTokenResponse = await instance.acquireTokenSilent({
            ...protectedApiRequest,
            account: currentAccount,
          });

          const responseApiInit = await apiInit.callProtectedApi(
            accessTokenResponse.accessToken,
            instance,
            protectedApiRequest,
            accounts,
            userEmail,
          );

          if (responseApiInit) {
            const response = await apiInit.GET_INIT_DATA_USER.getInitDataUser();
            const responseDataInit =
              typeof response.json === "function"
                ? await response.json()
                : response;
            const responseParams =
              await apiInit.GET_INIT_DATA_PARAMS.getParametrosGenerales();

            if (isMounted) {
              setInitData(responseDataInit);
              setMenus(responseDataInit.menus || []);
              if (responseParams) setParametros(responseParams);
            }
          } else {
            if (isMounted) setNoUserInBd(true);
          }
        } else {
          // Lógica de desarrollo (Dev / Local)
          if (userEmail) {
            const responseParams =
              await apiInit.GET_INIT_DATA_PARAMS.getParametrosGenerales();
            if (responseParams && isMounted) {
              setParametros(responseParams);
            }
          }

          const response =
            await apiInit.GET_INIT_DATA_USER_DEV.getInitDataUserDev(userEmail);
          const responseDataInit =
            typeof response.json === "function"
              ? await response.json()
              : response;

          if (isMounted) {
            console.log("Datos de inicialización (Dev):", responseDataInit);
            setInitData(responseDataInit);
            setMenus(responseDataInit.menus || []);
          }
        }
      } catch (error) {
        console.error("Error crítico en inicialización de contexto:", error);
        if (isMounted) setNoUserInBd(true);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initializeData();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, instance, accounts, inProgress]);

  if (!loading) {
    return (
      <InitDataContext.Provider
        value={{
          initData,
          setInitData,
          menus,
          setMenus,
          isTutorialActive,
          setIsTutorialActive,
          parametros,
          noUserInBd,
        }}
      >
        {children}
      </InitDataContext.Provider>
    );
  }

  return (
    <>
      {noUserInBd && <ErrorUsuAdNoBd />}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <Icon
          icon={"spinner"}
          className="fa-spin"
          style={{ fontSize: "50px" }}
        />
        {/* Un pequeño aviso temporal ayuda a diagnosticar si se queda colgado aquí */}
        <span style={{ fontFamily: "sans-serif", color: "#666" }}>
          Cargando sesión...
        </span>
      </div>
    </>
  );
};

export { InitDataContext, InitDataProvider };
